import { useEffect, useState } from "react";

import {
  ticketChatModeSendOptions,
  sameTicketChatMode,
  threadChatMode,
  type TicketChatAutomationMode,
  type TicketChatMode,
} from "../utils/ticketChatMode";

/**
 * The ticket chat's mode: what the thread runs as, unless the technician picked something else.
 *
 * A pick is held until the server reports it (same capacity and agent), the run ends, or the
 * thread changes. Only a held pick is sent, so a message never carries an implicit mode. Picking
 * another agent during a run hands the ticket over: the server cancels the run and starts the pick.
 */
export function useTicketChatMode(
  threadId: string | null,
  capacity: TicketChatAutomationMode | null,
  threadAgentId: number | null = null
) {
  const [selection, setSelection] = useState<{ threadId: string | null; mode: TicketChatMode } | null>(null);
  useEffect(() => {
    setSelection((current) =>
      current &&
      (current.threadId !== threadId ||
        capacity === 0 ||
        (capacity !== null && sameTicketChatMode(current.mode, threadChatMode(capacity, threadAgentId))))
        ? null
        : current
    );
  }, [threadId, capacity, threadAgentId]);
  const selectedMode = selection?.threadId === threadId ? selection.mode : undefined;
  const threadMode = capacity === null ? null : threadChatMode(capacity, threadAgentId);
  const mode = selectedMode ?? threadMode;
  const setMode = (value: TicketChatMode) => setSelection({ threadId, mode: value });
  // Normal, or a different agent, during a run ends that run: the server cancels it, so the
  // message can be sent while the run is still working.
  const sendOptions = selectedMode === undefined ? undefined : {
    ...ticketChatModeSendOptions(selectedMode),
    interruptAutoRun: capacity === 3 && threadMode !== null && !sameTicketChatMode(selectedMode, threadMode),
  };
  return [mode, setMode, sendOptions] as const;
}
